import React, { useState, useMemo } from 'react';
import { 
  Plus, Trash2, Search, SlidersHorizontal, Gauge, ChevronDown, ChevronUp, 
  ArrowUpDown, Wrench, FileText, CheckCircle, AlertTriangle, AlertOctagon, HelpCircle, WifiOff 
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import VidangeForm from './VidangeForm';
import DocumentViewerModal from '../../shared/components/DocumentViewerModal';

// ─────────────────────────────────────────────
// Linear Progress Bar Component
// ─────────────────────────────────────────────
function ProgressBar({ pct, color }) {
  return (
    <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700 ease-out"
        style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// Define Maintenance Types
// ─────────────────────────────────────────────
const MAINTENANCE_TYPES = [
  { key: 'Vidange', fr: 'Vidange d\'huile', ar: 'تغيير الزيت' },
  { key: 'Filtre à huile', fr: 'Filtre à huile', ar: 'فلتر الزيت' },
  { key: 'Filtre à air', fr: 'Filtre à air', ar: 'فلتر الهواء' },
  { key: 'Filtre à carburant', fr: 'Filtre à carburant', ar: 'فلتر الوقود' },
  { key: 'Courroie de distribution', fr: 'Courroie de distribution', ar: 'حزام التوقيت' },
  { key: 'Pneus', fr: 'Pneus', ar: 'الإطارات' },
  { key: 'Freins', fr: 'Freins', ar: 'الفرامل' },
  { key: 'Bougies', fr: 'Bougies', ar: 'الشمعات' },
  { key: 'Batterie', fr: 'Batterie', ar: 'البطارية' },
  { key: 'Climatisation', fr: 'Climatisation', ar: 'التكييف' },
  { key: 'Autre', fr: 'Autre', ar: 'أخرى' }
];

// ─────────────────────────────────────────────
// Calculate Detailed Maintenance State per Car
// ─────────────────────────────────────────────
function getCarMaintenanceState(car, vidanges, settings) {
  const effectiveKm = Math.round((parseFloat(car.km) || 0) + (parseFloat(car.gpsOdometer) || 0));

  const maintWarning = settings.maintWarningThreshold ?? 80;
  const maintCritical = settings.maintCriticalThreshold ?? 90;
  const maintOverdue = settings.maintOverdueThreshold ?? 100;

  const getDefaultInterval = (type) => {
    const intervals = settings?.maintIntervals || {};
    if (intervals[type] !== undefined) return parseFloat(intervals[type]) || 10000;
    switch(type) {
      case 'Vidange':
      case 'Filtre à huile': return 10000;
      case 'Filtre à air':
      case 'Climatisation': return 20000;
      case 'Filtre à carburant':
      case 'Freins':
      case 'Bougies': return 30000;
      case 'Pneus': return 40000;
      case 'Batterie': return 60000;
      case 'Courroie de distribution': return 80000;
      default: return 10000;
    }
  };

  const carVidanges = vidanges.filter(v => v.carId === car.id || v.carId === car._id);

  let overdueCount = 0;
  let criticalCount = 0;
  let warningCount = 0;
  let goodCount = 0;
  let unknownCount = 0;

  const items = MAINTENANCE_TYPES.map(typeDef => {
    const typeMaint = carVidanges.filter(v => v.type === typeDef.key);
    typeMaint.sort((a, b) => (parseFloat(b.currentKm) || 0) - (parseFloat(a.currentKm) || 0));
    const latest = typeMaint[0] || null;

    let lastKm = 0;
    let nextKm = 0;
    let lastDate = 'N/A';
    let nextDate = 'N/A';
    let hasRecord = false;

    if (latest) {
      lastKm = parseFloat(latest.currentKm) || 0;
      nextKm = parseFloat(latest.nextKm) || 0;
      lastDate = latest.date || '—';
      nextDate = latest.nextDate || '—';
      hasRecord = true;
    } else {
      lastKm = 0;
      nextKm = getDefaultInterval(typeDef.key);
    }

    const interval = nextKm - lastKm;
    const driven = Math.max(0, effectiveKm - lastKm);
    const remaining = nextKm - effectiveKm;
    const pct = interval > 0 ? Math.round((driven / interval) * 100) : 0;

    let status = 'good';
    let color = '#10b981'; // green
    if (pct >= maintOverdue) {
      status = 'overdue'; color = '#ef4444'; overdueCount++;
    } else if (pct >= maintCritical) {
      status = 'critical'; color = '#f97316'; criticalCount++;
    } else if (pct >= maintWarning) {
      status = 'warning'; color = '#f59e0b'; warningCount++;
    } else {
      if (hasRecord) {
        goodCount++;
      } else {
        status = 'unknown'; color = '#9ca3af'; unknownCount++;
      }
    }

    return {
      key: typeDef.key,
      fr: typeDef.fr,
      ar: typeDef.ar,
      lastKm,
      nextKm,
      lastDate,
      nextDate,
      interval,
      driven,
      remaining,
      pct,
      status,
      color,
      hasRecord,
      latest
    };
  });

  // Calculate overall status based on worst item
  let overallStatus = 'good';
  let overallColor = '#10b981';
  let overallIcon = '✅';
  if (overdueCount > 0) {
    overallStatus = 'overdue'; overallColor = '#ef4444'; overallIcon = '🔴';
  } else if (criticalCount > 0) {
    overallStatus = 'critical'; overallColor = '#f97316'; overallIcon = '🟠';
  } else if (warningCount > 0) {
    overallStatus = 'warning'; overallColor = '#f59e0b'; overallIcon = '⚠️';
  } else if (goodCount === 0 && unknownCount > 0) {
    overallStatus = 'unknown'; overallColor = '#9ca3af'; overallIcon = '❓';
  }

  // Check GPS connection warning.
  // Align with the GPS page: TRUST the provider's device status flag first, and
  // fall back to the last COMMUNICATION time (gpsLastSeen), NOT the GPS fix time
  // (gpsUpdated). A parked-but-online vehicle keeps communicating while its fix
  // timestamp stays old — using gpsUpdated wrongly flagged it "GPS Déconnecté".
  let gpsStatus = 'manual'; // manual | online | offline
  if (car.gpsOdometer > 0) {
    gpsStatus = 'online';
    if (car.gpsDeviceStatus === 'offline') {
      gpsStatus = 'offline';
    } else if (car.gpsDeviceStatus === 'online') {
      gpsStatus = 'online';
    } else {
      // Provider status unknown → use communication-timeout as a last resort.
      const lastComm = car.gpsLastSeen || car.gpsUpdated;
      if (lastComm) {
        const offlineMs = (parseFloat(settings?.gpsOfflineMin) || 10) * 60 * 1000;
        if (new Date() - new Date(lastComm) > offlineMs) {
          gpsStatus = 'offline';
        }
      }
    }
  }

  return {
    effectiveKm,
    overallStatus,
    overallColor,
    overallIcon,
    gpsStatus,
    items,
    overdueCount,
    criticalCount,
    warningCount,
    goodCount,
    unknownCount
  };
}

// ─────────────────────────────────────────────
// Items Breakdown Component (Accordion content)
// ─────────────────────────────────────────────
function MaintenanceItemsList({ items, isAr, effectiveKm }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800">
      {items.map(item => {
        const itemLabel = isAr ? item.ar : item.fr;
        const remainingLabel = isAr ? 'المتبقي' : 'Restant';
        const drivenLabel = isAr ? 'منذ آخر صيانة' : 'Depuis dernier entretien';
        const statusText = 
          item.status === 'overdue' ? (isAr ? 'متأخر جداً' : 'En retard') :
          item.status === 'critical' ? (isAr ? 'حرج' : 'Critique') :
          item.status === 'warning' ? (isAr ? 'قريب' : 'Bientôt') :
          item.status === 'good' ? (isAr ? 'جيد' : 'Bon état') : (isAr ? 'غير محدد' : 'Non défini');

        return (
          <div key={item.key} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-150 dark:border-gray-700 shadow-xs flex flex-col justify-between">
            {/* Header info */}
            <div className="flex justify-between items-start mb-1.5">
              <div>
                <span className="text-xs font-black text-gray-950 dark:text-white">{itemLabel}</span>
                <span className="block text-[9px] text-gray-400 font-mono">
                  {isAr ? 'آخر صيانة:' : 'Dernier:'} {item.hasRecord ? `${Math.round(item.lastKm).toLocaleString()} km` : '—'}
                </span>
              </div>
              <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                item.status === 'overdue' ? 'bg-red-100 text-red-700' :
                item.status === 'critical' ? 'bg-orange-100 text-orange-700' :
                item.status === 'warning' ? 'bg-amber-100 text-amber-700' :
                item.status === 'good' ? 'bg-emerald-100 text-emerald-700' :
                'bg-gray-100 text-gray-500'
              }`}>
                {statusText}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="my-1.5">
              <div className="flex justify-between text-[9px] font-bold text-gray-500 mb-0.5">
                <span>{item.pct}%</span>
                <span>{Math.round(item.nextKm).toLocaleString()} km</span>
              </div>
              <ProgressBar pct={item.pct} color={item.color} />
            </div>

            {/* Footnote stats */}
            <div className="flex justify-between text-[9px] text-gray-400 mt-1">
              <span>{drivenLabel}: {Math.round(item.driven).toLocaleString()} km</span>
              <span className={`font-bold ${
                item.status === 'overdue' ? 'text-red-600' :
                item.status === 'critical' ? 'text-orange-600' :
                item.status === 'warning' ? 'text-amber-600' : 'text-emerald-600'
              }`}>
                {remainingLabel}: {item.status === 'overdue' ? `+${Math.round(Math.abs(item.remaining)).toLocaleString()} km ⚠️` : `${Math.round(item.remaining).toLocaleString()} km`}
              </span>
            </div>

            {/* Dates */}
            <div className="flex justify-between text-[9px] text-gray-400 border-t border-gray-50 dark:border-gray-700/50 pt-1 mt-1 font-mono">
              <span>📅 {item.lastDate}</span>
              {item.nextDate !== '—' && <span>⏳ {item.nextDate}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// Single Card View Component
// ─────────────────────────────────────────────
function VehicleCard({ car, state, settings, isAr, onAddMaint }) {
  const [expanded, setExpanded] = useState(false);

  const overallStatusLabel = isAr
    ? (state.overallStatus === 'good' ? 'ممتاز' : state.overallStatus === 'warning' ? 'قريب' : state.overallStatus === 'critical' ? 'حرج' : state.overallStatus === 'overdue' ? 'متأخر' : 'غير محدد')
    : (state.overallStatus === 'good' ? 'En ordre' : state.overallStatus === 'warning' ? 'Bientôt' : state.overallStatus === 'critical' ? 'Critique' : state.overallStatus === 'overdue' ? 'En retard' : 'Non défini');

  // Find due items
  const dueItems = state.items.filter(i => i.status === 'overdue' || i.status === 'critical' || i.status === 'warning');

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-2 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md ${
      state.overallStatus === 'overdue' ? 'border-red-200 dark:border-red-900/50' :
      state.overallStatus === 'critical' ? 'border-orange-200 dark:border-orange-900/50' :
      state.overallStatus === 'warning' ? 'border-amber-200 dark:border-amber-900/50' :
      'border-gray-150 dark:border-gray-700'
    }`}>
      {/* Card Header */}
      <div className={`px-4 py-3 flex items-center justify-between border-b ${
        state.overallStatus === 'overdue' ? 'bg-red-50/50 dark:bg-red-950/20 border-red-100 dark:border-red-900/20' :
        state.overallStatus === 'critical' ? 'bg-orange-50/50 dark:bg-orange-950/20 border-orange-100 dark:border-orange-900/20' :
        state.overallStatus === 'warning' ? 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-100 dark:border-amber-900/20' :
        'bg-gray-50/50 dark:bg-gray-900/20 border-gray-150 dark:border-gray-700'
      }`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
            style={{ backgroundColor: `${state.overallColor}18`, color: state.overallColor }}>
            🚗
          </div>
          <div className="min-w-0">
            <h3 className="font-black text-gray-900 dark:text-white text-sm leading-tight truncate">
              {car.brand} {car.model}
            </h3>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono font-semibold">{car.plate}</span>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 ${
            state.overallStatus === 'overdue' ? 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400 animate-pulse' :
            state.overallStatus === 'critical' ? 'bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400' :
            state.overallStatus === 'warning' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400' :
            state.overallStatus === 'good' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400' :
            'bg-gray-100 text-gray-500 dark:bg-gray-900/30 dark:text-gray-400'
          }`}>
            <span>{state.overallIcon}</span>
            <span>{overallStatusLabel}</span>
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-3">
        {/* GPS status and mileage */}
        <div className="flex justify-between items-center text-xs">
          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
            <Gauge className="w-3.5 h-3.5" />
            <span>{isAr ? 'المسافة الإجمالية:' : 'Kilométrage total :'}</span>
          </div>
          <span className="font-bold text-gray-950 dark:text-white font-mono">{Math.round(state.effectiveKm).toLocaleString()} km</span>
        </div>

        {/* GPS Badge / Offline status */}
        <div className="flex flex-wrap items-center gap-1.5">
          {state.gpsStatus === 'online' && (
            <span className="text-[9px] font-bold px-2 py-0.5 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-900 rounded-full flex items-center gap-1">
              📡 GPS ({Math.round(car.gpsOdometer).toLocaleString()} km)
            </span>
          )}
          {state.gpsStatus === 'offline' && (
            <span className="text-[9px] font-bold px-2 py-0.5 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border border-red-150 dark:border-red-900 rounded-full flex items-center gap-1 animate-pulse">
              <WifiOff className="w-2.5 h-2.5" /> {isAr ? 'تنبيه: GPS غير متصل' : 'GPS Déconnecté'} ({Math.round(car.gpsOdometer).toLocaleString()} km)
            </span>
          )}
          {state.gpsStatus === 'manual' && (
            <span className="text-[9px] font-bold px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full flex items-center gap-1">
              🔧 {isAr ? 'تتبع يدوي' : 'Suivi manuel'}
            </span>
          )}
          {car.km > 0 && (
            <span className="text-[9px] font-semibold px-2 py-0.5 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full">
              {isAr ? 'البداية:' : 'Départ:'} {Math.round(car.km).toLocaleString()} km
            </span>
          )}
        </div>

        {/* Due alerts short summary */}
        {dueItems.length > 0 ? (
          <div className="p-2.5 bg-amber-50/50 dark:bg-amber-950/10 border border-amber-100 dark:border-amber-900/50 rounded-xl space-y-1">
            <div className="text-[10px] font-bold text-amber-800 dark:text-amber-400 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>{isAr ? 'تنبيهات صيانة مطلوبة:' : 'Entretiens requis :'}</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {dueItems.map(i => (
                <span key={i.key} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                  i.status === 'overdue' ? 'bg-red-500 text-white' :
                  i.status === 'critical' ? 'bg-orange-500 text-white' :
                  'bg-amber-500 text-white'
                }`}>
                  {isAr ? i.ar : i.fr}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-[10px] text-green-700 dark:text-green-400 bg-green-50/30 dark:bg-green-950/5 p-2 rounded-lg font-bold flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5" />
            <span>{isAr ? 'جميع إعدادات الصيانة جيدة' : 'Tous les entretiens sont à jour'}</span>
          </div>
        )}

        {/* Accordion trigger */}
        <div className="pt-2 border-t border-gray-100 dark:border-gray-700/50 flex gap-2">
          <button 
            onClick={() => setExpanded(!expanded)}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl text-xs font-bold text-gray-700 dark:text-gray-300 transition"
          >
            <span>{isAr ? 'تفاصيل الصيانة (11)' : 'Détails maintenance (11)'}</span>
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          
          <button
            onClick={() => onAddMaint(car.id)}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition flex items-center justify-center shadow-xs"
            title={isAr ? 'تسجيل صيانة' : 'Enregistrer entretien'}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Collapsible Panel */}
        {expanded && (
          <MaintenanceItemsList 
            items={state.items} 
            isAr={isAr} 
            effectiveKm={state.effectiveKm} 
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Page Component
// ─────────────────────────────────────────────
export default function VidangePage({ settings }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';

  const { data: vidanges, add: addVidange, remove: removeVidange, loading: vidangesLoading } = useCollection('vidanges');
  const { data: cars, update: updateCar, loading: carsLoading } = useCollection('cars');

  const [showForm, setShowForm] = useState(false);
  const [viewDoc, setViewDoc] = useState(null); // maintenance invoice opened via signed-URL viewer
  const [selectedCarId, setSelectedCarId] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('urgency'); // urgency | name | remaining
  const [sortDir, setSortDir] = useState('asc');
  const [view, setView] = useState('cards'); // cards | table
  const [expandedRows, setExpandedRows] = useState({});

  // ── Handlers ──
  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete') || (isAr ? 'هل تريد حذف هذا السجل؟' : 'Supprimer cette maintenance?'))) {
      try { 
        await removeVidange(id); 
      } catch (err) { 
        alert('❌ ' + err.message); 
      }
    }
  };

  const handleSave = async (payload) => {
    try {
      await addVidange(payload);
      
      // Update primary car mileage indicators if it's the standard Vidange type
      if (payload.type === 'Vidange') {
        await updateCar(payload.carId, { km: payload.currentKm, vidangeNextKm: payload.nextKm });
      }

      await sendTelegramNotification(
        buildTelegramMessage('vidange_done', {
          carName: payload.carName, typeKey: payload.type,
          currentKm: payload.currentKm, nextKm: payload.nextKm,
          cost: payload.cost, currency: settings.currency
        }),
        'vidange_done'
      );
    } catch (err) { 
      alert('❌ ' + err.message); 
    }
  };

  const openFormWithCar = (carId) => {
    setSelectedCarId(carId);
    setShowForm(true);
  };

  const toggleRowExpanded = (carId) => {
    setExpandedRows(prev => ({ ...prev, [carId]: !prev[carId] }));
  };

  // ── Process cars with overall maintenance calculations ──
  const processedCars = useMemo(() => {
    return cars.map(car => {
      const state = getCarMaintenanceState(car, vidanges, settings);
      return { ...car, _state: state };
    });
  }, [cars, vidanges, settings]);

  // ── Filter & Search & Sort ──
  const displayCars = useMemo(() => {
    let list = processedCars.filter(car => {
      const q = search.toLowerCase();
      const matchesSearch = !q || `${car.brand} ${car.model} ${car.plate}`.toLowerCase().includes(q);
      
      let matchesFilter = true;
      if (filterStatus === 'overdue') {
        matchesFilter = car._state.overallStatus === 'overdue';
      } else if (filterStatus === 'soon') {
        matchesFilter = car._state.overallStatus === 'critical' || car._state.overallStatus === 'warning';
      } else if (filterStatus === 'good') {
        matchesFilter = car._state.overallStatus === 'good';
      } else if (filterStatus === 'unknown') {
        matchesFilter = car._state.overallStatus === 'unknown';
      }

      return matchesSearch && matchesFilter;
    });

    list.sort((a, b) => {
      let va, vb;
      if (sortBy === 'urgency') {
        const order = { overdue: 0, critical: 1, warning: 2, good: 3, unknown: 4 };
        va = order[a._state.overallStatus] ?? 5;
        vb = order[b._state.overallStatus] ?? 5;
      } else if (sortBy === 'remaining') {
        // Sort by minimum remaining kilometers of any item
        const minRemainingA = Math.min(...a._state.items.map(i => i.remaining));
        const minRemainingB = Math.min(...b._state.items.map(i => i.remaining));
        va = minRemainingA;
        vb = minRemainingB;
      } else {
        va = `${a.brand} ${a.model}`;
        vb = `${b.brand} ${b.model}`;
      }

      if (typeof va === 'string') {
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });

    return list;
  }, [processedCars, search, filterStatus, sortBy, sortDir]);

  // ── Summary aggregate statistics ──
  const counts = useMemo(() => {
    let overdue = 0;
    let soon = 0;
    let good = 0;
    let unknown = 0;

    processedCars.forEach(car => {
      if (car._state.overallStatus === 'overdue') overdue++;
      else if (car._state.overallStatus === 'critical' || car._state.overallStatus === 'warning') soon++;
      else if (car._state.overallStatus === 'good') good++;
      else unknown++;
    });

    return { overdue, soon, good, unknown };
  }, [processedCars]);

  if (vidangesLoading || carsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900 dark:text-white leading-tight flex items-center gap-2">
            <span>⚙️</span>
            <span>{isAr ? 'إدارة الصيانة وتغيير الزيت' : 'Maintenance & Vidanges'}</span>
          </h1>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {isAr
              ? `${cars.length} سيارة • ${vidanges.length} سجل صيانة`
              : `${cars.length} véhicules • ${vidanges.length} entrées de maintenance`}
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedCarId('');
            setShowForm(true);
          }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold shadow-sm transition-all"
        >
          <Plus className="w-4 h-4" />
          {t('add_maintenance') || (isAr ? 'تسجيل صيانة جديدة' : 'Ajouter entretien')}
        </button>
      </div>

      {/* ── Aggregate Status Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { key: 'overdue', label: isAr ? '🔴 متأخر' : '🔴 En retard', count: counts.overdue, color: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400' },
          { key: 'soon', label: isAr ? '⚠️ قريب' : '⚠️ Bientôt', count: counts.soon, color: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400' },
          { key: 'good', label: isAr ? '✅ جاهز' : '✅ À jour', count: counts.good, color: 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400' },
          { key: 'unknown', label: isAr ? '❓ غير محدد' : '❓ Non défini', count: counts.unknown, color: 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400' }
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setFilterStatus(filterStatus === s.key ? 'all' : s.key)}
            className={`border-2 rounded-2xl p-4 text-left transition-all relative overflow-hidden ${s.color} ${
              filterStatus === s.key ? 'ring-2 ring-offset-2 ring-blue-500' : 'hover:scale-[1.02] shadow-xs'
            }`}
          >
            <div className="text-3xl font-black leading-none">{s.count}</div>
            <div className="text-xs font-bold mt-1.5 flex items-center gap-1">{s.label}</div>
          </button>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search bar */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={isAr ? 'البحث عن سيارة...' : 'Rechercher un véhicule (Marque, Modèle, Matricule)...'}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none text-gray-900 dark:text-white transition"
            />
          </div>

          {/* Sort selection */}
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={() => {
                if (sortBy === 'urgency') setSortBy('remaining');
                else if (sortBy === 'remaining') setSortBy('name');
                else setSortBy('urgency');
              }}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              <span>
                {sortBy === 'urgency' ? (isAr ? 'ترتيب: الاستعجال' : 'Trier: Urgence') :
                 sortBy === 'remaining' ? (isAr ? 'ترتيب: المتبقي' : 'Trier: Restant') :
                 (isAr ? 'ترتيب: الاسم' : 'Trier: Nom')}
              </span>
            </button>

            <button
              onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              {sortDir === 'asc' ? '↑' : '↓'}
            </button>

            {/* View toggle */}
            <button
              onClick={() => setView(v => v === 'cards' ? 'table' : 'cards')}
              className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition flex items-center gap-1"
            >
              <span>{view === 'cards' ? '📋 ' + (isAr ? 'جدول' : 'Tableau') : '⊞ ' + (isAr ? 'بطاقات' : 'Cartes')}</span>
            </button>
          </div>
        </div>

        {/* Sub-filtering buttons */}
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50">
          {[
            { key: 'all', label: isAr ? 'الكل' : 'Tous les véhicules' },
            { key: 'overdue', label: isAr ? '🔴 متأخر' : '🔴 En retard' },
            { key: 'soon', label: isAr ? '⚠️ قريب' : '⚠️ Bientôt' },
            { key: 'good', label: isAr ? '✅ جاهز' : '✅ À jour' },
            { key: 'unknown', label: isAr ? '❓ غير محدد' : '❓ Non défini' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilterStatus(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                filterStatus === f.key
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ms-auto text-xs font-bold text-gray-400 dark:text-gray-500 self-center">
            {displayCars.length} {isAr ? 'نتائج' : 'résultat(s)'}
          </span>
        </div>
      </div>

      {/* ── GPS Synced Warning Notice ── */}
      {cars.some(c => c.gpsOdometer > 0) && (
        <div className="flex items-center gap-3 text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900 rounded-2xl px-4 py-3">
          <span className="text-lg animate-pulse">📡</span>
          <div>
            <span className="font-bold block">{isAr ? 'تحديث الكيلومترات التلقائي نشط' : 'Synchronisation GPS temps réel active'}</span>
            <span className="opacity-80 block text-[10px] mt-0.5">
              {isAr 
                ? 'يتم حساب المسافة المقطوعة مباشرة من منصة تتبع السيارات وتحديث مؤشر الصيانة فوراً.'
                : 'La distance parcourue est lue de vos traceurs GPS et synchronisée instantanément avec les indicateurs.'}
            </span>
          </div>
        </div>
      )}

      {/* ── CARDS VIEW ── */}
      {view === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {displayCars.map(car => (
            <VehicleCard
              key={car.id}
              car={car}
              state={car._state}
              settings={settings}
              isAr={isAr}
              onAddMaint={openFormWithCar}
            />
          ))}
          {displayCars.length === 0 && (
            <div className="col-span-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-150 dark:border-gray-700 py-16 text-center text-gray-400">
              <div className="text-4xl mb-2">🔍</div>
              <p className="text-sm font-semibold">{isAr ? 'لا توجد نتائج مطابقة' : 'Aucun véhicule ne correspond aux critères.'}</p>
            </div>
          )}
        </div>
      )}

      {/* ── TABLE VIEW ── */}
      {view === 'table' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'السيارة' : 'Véhicule'}</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'الكيلومترات' : 'Kilométrage'}</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'تنبيهات مطلوبة' : 'Alertes actives'}</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'الحالة' : 'Statut Global'}</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {displayCars.map(car => {
                  const state = car._state;
                  const isExpanded = !!expandedRows[car.id];
                  const dueItems = state.items.filter(i => i.status === 'overdue' || i.status === 'critical' || i.status === 'warning');

                  return (
                    <React.Fragment key={car.id}>
                      <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-950/20 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-bold text-sm text-gray-950 dark:text-white">{car.brand} {car.model}</div>
                          <div className="text-xs text-gray-400 font-mono mt-0.5">{car.plate}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-bold text-xs text-gray-900 dark:text-white font-mono">{Math.round(state.effectiveKm).toLocaleString()} km</div>
                          <div className="mt-1 flex gap-1">
                            {state.gpsStatus === 'online' && (
                              <span className="text-[9px] font-bold text-blue-600 bg-blue-50 dark:bg-blue-950/30 px-1.5 py-0.5 rounded">📡 GPS</span>
                            )}
                            {state.gpsStatus === 'offline' && (
                              <span className="text-[9px] font-bold text-red-600 bg-red-50 dark:bg-red-950/30 px-1.5 py-0.5 rounded animate-pulse">📡 WARNING</span>
                            )}
                            {state.gpsStatus === 'manual' && (
                              <span className="text-[9px] font-bold text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">🔧 Manuel</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {dueItems.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {dueItems.map(i => (
                                <span key={i.key} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                  i.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                  i.status === 'critical' ? 'bg-orange-100 text-orange-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {isAr ? i.ar : i.fr}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-green-600 font-medium">✓ A jour</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 w-max ${
                            state.overallStatus === 'overdue' ? 'bg-red-100 text-red-700 dark:bg-red-950/30' :
                            state.overallStatus === 'critical' ? 'bg-orange-100 text-orange-700 dark:bg-orange-950/30' :
                            state.overallStatus === 'warning' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/30' :
                            state.overallStatus === 'good' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            <span>{state.overallIcon}</span>
                            <span>
                              {state.overallStatus === 'good' ? (isAr ? 'جيد' : 'À jour') :
                               state.overallStatus === 'warning' ? (isAr ? 'قريب' : 'Bientôt') :
                               state.overallStatus === 'critical' ? (isAr ? 'حرج' : 'Critique') :
                               state.overallStatus === 'overdue' ? (isAr ? 'متأخر' : 'En retard') : 'N/A'}
                            </span>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openFormWithCar(car.id)}
                              className="bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition"
                              title={isAr ? 'إضافة صيانة' : 'Enregistrer entretien'}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => toggleRowExpanded(car.id)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-2 rounded-lg"
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={5} className="px-6 py-3 bg-gray-50/50 dark:bg-gray-900/10 border-t border-b border-gray-100 dark:border-gray-700">
                            <div className="p-2">
                              <h4 className="text-xs font-black text-gray-700 dark:text-gray-300 mb-3">{isAr ? 'تفاصيل حالة الصيانة التامة للسيارة:' : 'État détaillé des 11 entretiens du véhicule :'}</h4>
                              <MaintenanceItemsList items={state.items} isAr={isAr} effectiveKm={state.effectiveKm} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {displayCars.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-400 font-semibold">{isAr ? 'لا توجد نتائج مطابقة' : 'Aucun véhicule trouvé.'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Maintenance History Table ── */}
      {vidanges.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-bold text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
              📋 {isAr ? 'سجل الصيانة الكامل للأسطول' : 'Historique complet des entretiens de la flotte'}
              <span className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded-full font-bold">{vidanges.length}</span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'السيارة' : 'Véhicule'}</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'النوع' : 'Type d\'entretien'}</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'التاريخ' : 'Date'}</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'المسافة المسجلة' : 'Kilométrage'}</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'المسافة التالية' : 'Prochaine limite'}</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'التكلفة' : 'Coût'}</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'قطع الغيار والقطع المستبدلة' : 'Pièces remplacées'}</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{isAr ? 'الفاتورة' : 'Facture'}</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {[...vidanges].sort((a, b) => (b.date || '').localeCompare(a.date || '')).map(v => {
                  const typeLabel = MAINTENANCE_TYPES.find(m => m.key === v.type)?.[lang === 'ar' ? 'ar' : 'fr'] || v.type;
                  return (
                    <tr key={v.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-950/10 transition-colors">
                      <td className="px-6 py-3.5 font-bold text-sm text-gray-900 dark:text-white">{v.carName}</td>
                      <td className="px-6 py-3.5">
                        <span className="text-xs font-bold px-2 py-0.5 bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400 rounded-lg border border-purple-100 dark:border-purple-900/50">
                          {typeLabel}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-xs text-gray-500 dark:text-gray-400 font-mono">{v.date}</td>
                      <td className="px-6 py-3.5 text-xs font-mono text-gray-700 dark:text-gray-300">{v.currentKm?.toLocaleString()} km</td>
                      <td className="px-6 py-3.5 text-xs font-mono font-bold text-blue-600 dark:text-blue-400">{v.nextKm?.toLocaleString()} km</td>
                      <td className="px-6 py-3.5 text-xs font-bold font-mono text-gray-900 dark:text-white">{v.cost} {settings.currency}</td>
                      <td className="px-6 py-3.5 text-xs text-gray-600 dark:text-gray-400 max-w-[180px] truncate" title={v.parts}>{v.parts || '—'}</td>
                      <td className="px-6 py-3.5">
                        {v.invoiceUrl ? (
                          <button
                            type="button"
                            onClick={() => setViewDoc({ url: v.invoiceUrl, title: isAr ? 'فاتورة الصيانة' : 'Facture entretien' })}
                            className="inline-flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900 px-2.5 py-1 rounded-lg text-[10px] font-bold hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            <span>{isAr ? 'تحميل' : 'Voir'}</span>
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <button 
                          onClick={() => handleDelete(v.id)} 
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 p-2 rounded-xl transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <VidangeForm
          cars={cars}
          settings={settings}
          onSave={handleSave}
          onClose={() => setShowForm(false)}
        />
      )}

      {viewDoc && (
        <DocumentViewerModal
          url={viewDoc.url}
          title={viewDoc.title}
          onClose={() => setViewDoc(null)}
        />
      )}
    </div>
  );
}
